<template>
  <div class="bhe-view">

    <!-- ─── Top bar ─── -->
    <div class="bhe-topbar">
      <vs-button
        color="#6a1f4c"
        type="filled"
        class="bhe-save-btn"
        @click="handleSaveClick"
      >
        {{ $t('dash.presenceManagement.hours.save') }}
      </vs-button>
      <vs-button
        color="#6a1f4c"
        type="border"
        class="bhe-back-btn"
        @click="$router.push({ name: 'presence-management' })"
      >
        {{ $t('dash.presenceManagement.hours.back') }}
      </vs-button>
    </div>

    <!-- ─── Network support info banner ─── -->
    <div class="bhe-notice-banner">
      <i class="material-icons bhe-notice-icon">info</i>
      <span v-html="$t('dash.presenceManagement.hours.networkNoticeHtml')"></span>
    </div>

    <!-- ─── Schedule ─── -->
    <div class="bhe-schedule">
      <div
        v-for="day in days"
        :key="day.key"
        class="bhe-day"
      >
        <div class="bhe-day-grid">
          <div class="bhe-day-toggles">
            <!-- Day label -->
            <p
              class="bhe-day-label"
              :class="{ 'bhe-day-label--open': schedule[day.value].isOpen }"
            >
              {{ day.label }}
            </p>
  
            <!-- Toggle: Ouvert / Fermé -->
            <div class="bhe-toggle-group">
              <vs-switch
                v-model="schedule[day.value].isOpen"
                color="#6a1f4c"
                @input="onToggleOpen(day.value, $event)"
              />
              <span class="bhe-toggle-label">
                {{ schedule[day.value].isOpen
                    ? $t('dash.presenceManagement.hours.open')
                    : $t('dash.presenceManagement.hours.closed') }}
              </span>
            </div>
  
            <!-- Toggle: 24/24h -->
            <div class="bhe-toggle-group">
              <vs-switch
                v-model="schedule[day.value].isAllDay"
                color="#6a1f4c"
                :disabled="!schedule[day.value].isOpen"
              />
              <span class="bhe-toggle-label bhe-toggle-label--secondary">
                {{ $t('dash.presenceManagement.hours.allDay') }}
              </span>
            </div>
          </div>

          <!-- Time slots (visible only when open and not allDay) -->
          <div class="bhe-slots">
            <template v-if="schedule[day.value].isOpen && !schedule[day.value].isAllDay">
              <div
                v-for="(slot, idx) in schedule[day.value].slots"
                :key="idx"
                class="bhe-slot-row"
              >
                <input
                  type="time"
                  v-model="slot.from"
                  class="bhe-time-input"
                  :class="{ 'bhe-time-input--error': hasFromError(day.value, idx) }"
                />
                <span class="bhe-time-sep">{{ $t('dash.presenceManagement.hours.to') }}</span>
                <input
                  type="time"
                  v-model="slot.to"
                  class="bhe-time-input"
                  :class="{ 'bhe-time-input--error': hasToError(day.value, idx) }"
                />
                <span class="bhe-slot-actions">
                  <button
                    v-if="idx > 0"
                    class="bhe-icon-btn bhe-icon-btn--del"
                    :title="$t('dash.presenceManagement.hours.removeSlot')"
                    @click="removeSlot(day.value, idx)"
                  >
                    <i class="material-icons">delete_outline</i>
                  </button>
                  <button
                    v-if="idx === 0 && schedule[day.value].slots.length < 4"
                    class="bhe-icon-btn bhe-icon-btn--add"
                    :title="$t('dash.presenceManagement.hours.addSlot')"
                    @click="addSlot(day.value)"
                  >
                    <i class="material-icons">add_circle_outline</i>
                  </button>
                </span>
              </div>
            </template>
          </div>

          <!-- Apply to all days -->
          <button class="bhe-apply-btn" @click="applyToAll(day.value)">
            <i class="material-icons">auto_fix_high</i>
            <span>{{ $t('dash.presenceManagement.hours.applyToAll') }}</span>
          </button>

        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { EventBus } from '@/event-bus'

const DAYS = [
  { key: 'monday',    value: 'MONDAY' },
  { key: 'tuesday',   value: 'TUESDAY' },
  { key: 'wednesday', value: 'WEDNESDAY' },
  { key: 'thursday',  value: 'THURSDAY' },
  { key: 'friday',    value: 'FRIDAY' },
  { key: 'saturday',  value: 'SATURDAY' },
  { key: 'sunday',    value: 'SUNDAY' }
]

const DAY_FALLBACK = {
  monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi',
  thursday: 'Jeudi', friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche'
}

function buildDefaultSchedule() {
  return {
    MONDAY:    { isOpen: false, isAllDay: false, slots: [{ from: '09:00', to: '18:00' }] },
    TUESDAY:   { isOpen: false, isAllDay: false, slots: [{ from: '09:00', to: '18:00' }] },
    WEDNESDAY: { isOpen: false, isAllDay: false, slots: [{ from: '09:00', to: '18:00' }] },
    THURSDAY:  { isOpen: false, isAllDay: false, slots: [{ from: '09:00', to: '18:00' }] },
    FRIDAY:    { isOpen: false, isAllDay: false, slots: [{ from: '09:00', to: '18:00' }] },
    SATURDAY:  { isOpen: false, isAllDay: false, slots: [{ from: '09:00', to: '18:00' }] },
    SUNDAY:    { isOpen: false, isAllDay: false, slots: [{ from: '09:00', to: '18:00' }] }
  }
}

function timeToMinutes(timeStr) {
  if (!timeStr) return 0
  const [hours, minutes] = timeStr.split(':').map(Number)
  return hours * 60 + minutes
}

export default {
  name: 'bulk-hours-editor',

  computed: {
    ...mapGetters('selectPage', {
      selectedPageIds: 'selectPagePageSelected',
      allPages: 'selectPageAllPages'
    }),

    days() {
      return DAYS.map(day => {
        const i18nKey = `dash.presenceManagement.days.full.${day.key}`
        const translated = this.$t(i18nKey)
        return { ...day, label: translated === i18nKey ? DAY_FALLBACK[day.key] : translated }
      })
    }
  },

  data() {
    return {
      schedule: buildDefaultSchedule()
    }
  },

  methods: {
    ...mapActions('presenceManagement', ['saveBulkSchedule']),

    onToggleOpen(dayKey, isOpen) {
      if (!isOpen) {
        this.schedule[dayKey].isAllDay = false
      }
    },

    addSlot(dayKey) {
      const slots = this.schedule[dayKey].slots
      if (slots.length >= 4) return

      const lastSlot = slots[slots.length - 1]
      let nextFrom = '09:00'
      let nextTo = '18:00'

      if (lastSlot && lastSlot.to) {
        const [hours, minutes] = lastSlot.to.split(':').map(Number)
        
        let newH = hours + 1
        let newM = minutes

        if (newH > 23) {
          newH = 23
          newM = 59
        }

        const pad = (n) => String(n).padStart(2, '0')
        nextFrom = `${pad(newH)}:${pad(newM)}`
        
        let endH = newH + 2
        if (endH > 23) {
          endH = 23
        }
        nextTo = `${pad(endH)}:${pad(newM)}`

        if (timeToMinutes(nextFrom) >= timeToMinutes(nextTo)) {
          nextFrom = lastSlot.to
          nextTo = '23:59'
        }
      }

      slots.push({ from: nextFrom, to: nextTo })
    },

    removeSlot(dayKey, idx) {
      this.schedule[dayKey].slots.splice(idx, 1)
    },

    hasFromError(dayValue, idx) {
      const config = this.schedule[dayValue]
      if (!config || !config.isOpen || config.isAllDay) return false

      const slots = config.slots
      const slot = slots[idx]
      if (!slot || !slot.from || !slot.to) return false

      const start = timeToMinutes(slot.from)
      const end = timeToMinutes(slot.to)

      // 1. A start time cannot be after or equal to its own end time
      if (start >= end) {
        return true
      }

      // 2. A start time cannot be before the previous slot's end time
      if (idx > 0 && slots[idx - 1]) {
        const prevEnd = timeToMinutes(slots[idx - 1].to)
        if (start < prevEnd) {
          return true
        }
      }

      return false
    },

    hasToError(dayValue, idx) {
      const config = this.schedule[dayValue]
      if (!config || !config.isOpen || config.isAllDay) return false

      const slots = config.slots
      const slot = slots[idx]
      if (!slot || !slot.from || !slot.to) return false

      const start = timeToMinutes(slot.from)
      const end = timeToMinutes(slot.to)

      // 1. An end time cannot be before or equal to its own start time
      if (start >= end) {
        return true
      }

      // 2. An end time cannot be after the next slot's start time
      if (idx < slots.length - 1 && slots[idx + 1]) {
        const nextStart = timeToMinutes(slots[idx + 1].from)
        if (end > nextStart) {
          return true
        }
      }

      return false
    },

    /**
     * Copies the source day's open/allDay/slots config to every other day.
     * $set is used to ensure Vue 2 reactivity on the replaced schedule entries.
     */
    applyToAll(sourceDayKey) {
      const source = this.schedule[sourceDayKey]
      DAYS.forEach(({ value }) => {
        if (value === sourceDayKey) return
        this.$set(this.schedule, value, {
          isOpen: source.isOpen,
          isAllDay: source.isAllDay,
          slots: source.slots.map(s => ({ from: s.from, to: s.to }))
        })
      })
    },

    handleSaveClick() {
      for (const day of this.days) {
        const dayKey = day.value
        const config = this.schedule[dayKey]

        if (config.isOpen && !config.isAllDay) {
          for (let i = 0; i < config.slots.length; i++) {
            if (this.hasFromError(dayKey, i) || this.hasToError(dayKey, i)) {
              this.$vs.notify({
                title: this.$t('dash.presenceManagement.hours.invalid.title'),
                // text: this.$t('dash.presenceManagement.hours.invalid.text', { day: day.label }),
                text: this.$t('dash.presenceManagement.hours.invalid.txt'),
                color: 'danger',
                position: 'top-right'
              })
              return
            }
          }
        }
      }

      EventBus.$emit(
        'showModal',
        this.$t('dash.presenceManagement.hours.confirm.title'),
        this.$t('dash.presenceManagement.hours.confirm.message'),
        this.$t('dash.presenceManagement.hours.confirm.accept'),
        this.$t('dash.presenceManagement.hours.confirm.cancel'),
        () => {
          this.onConfirmSave()
        }
      )
    },

    async onConfirmSave() {
      this.$vs.loading({ color: '#6a1f4c' })
      try {
        const results = await this.saveBulkSchedule({ schedule: this.schedule })
        
        const successes = (results || []).filter(r => r.success)
        const failures = (results || []).filter(r => !r.success)

        if (successes.length > 0) {
          this.$vs.notify({
            title: this.$t('dash.presenceManagement.hours.saveSuccess.title'),
            text: this.$t('dash.presenceManagement.hours.saveSuccess.text'),
            color: '#6a1f4c',
            position: 'top-right'
          })
        }

        if (failures.length > 0) {
          failures.forEach(fail => {
            const location = this.$store.state.presenceManagement.locations.find(loc => loc.id === fail.networkId)
            const title = location ? location.title : fail.networkId
            
            let errText = fail.error || 'Erreur inconnue de Google'
            if (fail.error === 'SPECIAL_HOURS_SET_WITHOUT_REGULAR_HOURS') {
              errText = 'Impossible de supprimer tous les horaires car des jours fériés ou exceptionnels sont configurés.'
            } else if (fail.error === 'PERMISSION_DENIED') {
              errText = 'Vous n\'avez pas l\'autorisation de modifier cet établissement sur Google.'
            }

            this.$vs.notify({
              title: title,
              text: errText,
              color: 'danger',
              position: 'top-right',
              time: 10000
            })
          })
        }

        this.$router.push({ name: 'presence-management', query: { saved: 'true' } })
      } catch (err) {
        console.error('Bulk hours update failed:', err)
        this.$vs.notify({
          title: this.$t('dash.presenceManagement.location.error.title'),
          text: this.$t('dash.presenceManagement.location.error.generic'),
          color: 'danger',
          position: 'top-right'
        })
      } finally {
        this.$vs.loading.close()
      }
    }
  }
}
</script>

<style scoped>
/* ─── Root ─── */
.bhe-view {
  width: 100%;
}

/* ─── Top bar ─── */
.bhe-topbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

/* ─── Schedule container ─── */
.bhe-schedule {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(46, 25, 55, 0.07);
}

/* ─── Day section ─── */
.bhe-day {
  background: #fff;
  min-height: 140px;
  border-bottom: 1px solid #f0e8f6;
}

.bhe-day:last-child {
  border-bottom: 0;
}

/* ─── Day grid ─── */
.bhe-day-grid {
  display: grid;
  grid-template-columns: 200px 1fr auto;
  align-items: center;
  padding: 18px 28px;
  gap: 0 20px;
}

/* ─── Day tittle + toggles block ─── */
.bhe-day-toggles {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ─── Day label ─── */
.bhe-day-label {
  margin: 0;
  font-size: 15px;
  font-weight: 400;
  color: #c0afd0;
  margin-bottom: 10px;
}

.bhe-day-label--open {
  font-weight: 700;
  color: #6a1f4c;
}

/* ─── Toggle groups ─── */
.bhe-toggle-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bhe-toggle-label {
  font-size: 13px;
  font-weight: 500;
  color: #3d2a4a;
  white-space: nowrap;
}

.bhe-toggle-label--secondary {
  color: #9e8dae;
}

/*
 * vs-switch in Vuesax 3 renders as a <button> element and inherits global button
 * styles (box-shadow, background-color from the Vuesax theme). These overrides
 * reset it to a standard pill toggle.
 */
::v-deep .bhe-toggle-group .vs-switch {
  background-color: #d8cce2 !important;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  border: none !important;
  min-width: 44px !important;
  width: 44px !important;
  height: 22px !important;
  border-radius: 11px !important;
  padding: 2px !important;
  flex-shrink: 0;
  position: relative !important;
}

::v-deep .bhe-toggle-group .vs-switch.vs-switch-active {
  background-color: #6a1f4c !important;
}

::v-deep .bhe-toggle-group .vs-switch[disabled] {
  background-color: #ece5f3 !important;
  opacity: 0.7 !important;
  cursor: not-allowed !important;
}

/* ─── Slots area ─── */
.bhe-slots {
  display: grid;
  grid-template-columns: repeat(2, auto);
  gap: 8px 16px;
  justify-content: start;
  align-items: center;
  margin-top: 8px;
}

.bhe-slot-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 20px;
}

/* ─── Time inputs ─── */
.bhe-time-input {
  border: 1px solid #e2d5ef;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  color: #3d2a4a;
  background: #fff;
  width: 100px;
  outline: none;
  cursor: text;
  transition: border-color 0.2s;
  box-shadow: none;
}

.bhe-time-input--error {
  border-color: #ff5c5c !important;
  color: #ff5c5c !important;
}

.bhe-time-input:focus {
  border-color: #6a1f4c;
}

/* Hide browser native clock picker icon */
.bhe-time-input::-webkit-calendar-picker-indicator {
  display: none;
}

.bhe-time-sep {
  font-size: 13px;
  color: #9e8dae;
}

/* ─── Slot action buttons ─── */
.bhe-slot-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
  min-width: 26px;
  flex-shrink: 0;
}

/*
 * Same issue as vs-switch: native <button> inherits Vuesax global button styles.
 * !important overrides are required here.
 */
button.bhe-icon-btn {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  border: none !important;
  padding: 4px !important;
  border-radius: 4px !important;
  min-width: 0 !important;
  width: auto !important;
  display: inline-flex !important;
  align-items: center !important;
  transition: color 0.18s;
}

button.bhe-icon-btn i {
  font-size: 14px;
}

button.bhe-icon-btn--del {
  color: #d4c1e3 !important;
}

button.bhe-icon-btn--del:hover {
  color: #6a1f4c !important;
}

button.bhe-icon-btn--add {
  color: #d4c1e3 !important;
}

button.bhe-icon-btn--add:hover {
  color: #6a1f4c !important;
}

/* ─── Apply to all button ─── */
button.bhe-apply-btn {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  border: none !important;
  color: #c9b4d8 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  padding: 6px 0 !important;
  min-width: 0 !important;
  width: auto !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
  white-space: nowrap !important;
  cursor: pointer !important;
  justify-self: end;
}

button.bhe-apply-btn:hover {
  color: #6a1f4c !important;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

button.bhe-apply-btn .material-icons {
  font-size: 18px;
}



/* ─── Responsive ─── */
@media (max-width: 1499px) {
  .bhe-slots {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

@media (max-width: 1149px) {
  .bhe-day-grid {
    grid-template-columns: 1fr;
    gap: 12px 0;
    padding: 16px 18px;
  }

  .bhe-slots {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  button.bhe-apply-btn {
    justify-self: start !important;
  }
}

.bhe-notice-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f2f6;
  border: 1px solid #e8dbe8;
  color: #5a4d64;
  padding: 14px 20px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 24px;
}

.bhe-notice-icon {
  font-size: 20px;
  color: #B64985;
  flex-shrink: 0;
}

.bhe-notice-banner /deep/ strong,
.bhe-notice-banner strong {
  font-weight: 600;
}
</style>
